import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { Translations } from '@ts-types/Translations'
import { getUrlByPageId } from '@utils/getUrlByPageId'
import { Languages } from '@wicadu/langscope'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Crear productos',
    seo_description:
      'Aprende cómo crear y editar productos en tu tienda paso a paso. Guía sencilla para agregar productos, editar detalles y tener tu catálogo listo para comenzar a vender en tu tienda en línea.',
    seo_keywords:
      'crear productos tienda en línea, cómo agregar productos en tienda online, gestionar productos wicadu, editar productos, agregar productos wicadu, guía para crear productos tienda online, administrar inventario tienda en línea, crear productos para tienda digital, gestionar productos comercio electrónico',
    page_id: PagesId.CreateItems,
    breadcrumbs: {
      home: 'Inicio',
      items_home: 'Productos e inventario',
      create_items: 'Crear productos',
    },
    article: {
      title: 'Crear productos',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para gestionar <em>inventario</em> y <em>productos</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST}>Asistente de wicadu</a>.`,
        `<strong>Vendedores</strong> o <strong>Abastecedor</strong> con permisos para <em>crear productos</em>, <em>actualizar características</em> y <em>asignar precios</em>.`,
      ],
      about: {
        title: 'Acerca de crear productos',
        content_type: ContentType.Paragraph,
        content: [
          '<strong>Crear productos</strong> es uno de los <em>primeros pasos</em> para operar tu <strong>tienda en línea</strong>. Cada producto representa una <b>oferta individual</b> en tu catálogo y puede ser un <em>artículo físico</em> o un <em>servicio</em>.',
          'En <strong>wicadu</strong>, el proceso de creación de productos está diseñado para ser <strong>rápido</strong>, <strong>flexible</strong> y <strong>eficiente</strong>. Esto te permite <em>mantener tu inventario actualizado</em>, <em>organizar tu catálogo de forma estratégica</em>  y ofrecer <strong>información clara y precisa</strong> a tus clientes en todos tus canales de venta.',
          'Al estructurar correctamente tus productos desde el inicio, mejoras la <strong>visibilidad de tu tienda</strong>, <em>reduces errores operativos</em> y ofreces una <strong>mejor experiencia de compra</strong>.'
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Antes de crear productos en tu tienda, asegúrate de haber considerado lo siguiente:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Fragment,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Las monedas de la tienda deben estar configurada ya que los precios y el stock se gestionarán utilizando estas monedas.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Nota</strong>: La moneda se asigna automáticamente según el país seleccionado al crear la tienda.',
                    ],
                  },

                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<em>Las especificaciones del producto</em> (como dimensiones, peso o materiales) son opcionales, pero se recomiendan para mayor claridad y configuración de envío.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'No puedes agregar stock ni realizar compras hasta que el producto haya sido creado.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Estos pasos aseguran que los <strong>productos</strong> estén listos para ser incluidos en tus <strong>compras</strong>.',
            ],
          },
        ],
      },
      how_to: {
        title: 'Pasos para crear un producto',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ir al módulo de Inventario: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Accede a la funcionalidad de <em>Inventario</em> dentro del <strong>Asistente de wicadu</strong>.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Crear un nuevo producto: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en el botón <b>+</b> ubicado en la esquina superior derecha de la página.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Identificar el producto: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'En la pantalla "<em>Identificar producto</em>", ingresa el nombre de tu producto. Esto buscará en tu catálogo cualquier producto existente con especificaciones similares que puedas utilizar como base. Luego, puedes:',
                ],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>Usar un producto similar como plantilla</b>: Selecciona un producto existente para usarlo como base y prellenar los campos.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>Crear desde cero</b>: Haz clic en "No es ninguno de los anteriores" para proceder desde cero y llenar todos los campos manualmente.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Nota</strong>: <em>Los nombres de los productos deben ser únicos</em>; aunque puedes ofrecer variantes (como tallas o colores), cada entrada de producto necesita un nombre distinto.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Rellenar los campos requeridos: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'En la pantalla "<em>Crear producto</em>" rellena o actualiza los campos requeridos según lo seleccionado anteriormente. Una vez que todos los campos requeridos estén completos, el botón <b>Continuar</b> estará habilitado.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Revisar y finalizar: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Revisa los detalles del producto y haz clic en <em>Continuar</em> para finalizar. Serás redirigido a la página de <em>Detalles del producto</em> donde podrás ver o actualizar más información.',
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Ahora que sabes como crear y editar los detalles generales de un producto, considera las siguientes acciones:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Agregar imagen del producto: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // ARTICLE IDEA
                      'Agrega una <strong>imagen del producto</strong> para mejorar el reconocimiento del cliente.'
                      // `Agrega una <strong>imagen del producto</strong> para mejorar el reconocimiento del cliente. Consulta
                      // <a href=${getUrlByPageId(PagesId.ItemsHome, Languages.Spanish)} title="Guía para agregar imágenes a un producto">Cómo agregar imágenes a un producto</a>. para más información`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Asignar precio: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Asigna un <em>precio</em> a tu producto para habilitar las ventas.`,
                      // `Asigna un <em>precio</em> a tu producto para habilitar las ventas. Aprende más sobre
                      // <a href=${getUrlByPageId(PagesId.ItemsHome, Languages.Spanish)} title="Guía para configurar precios">cómo configurar precios</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Editar detalles del producto: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Edita los detalles del producto según sea necesario. Aprende más sobre
                      <a href=${getUrlByPageId(PagesId.UpdateItems, Languages.Spanish)} title="Guía para editar productos">Cómo editar productos</a>.`,
                    ],
                  },
                ],
              },

              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Registra tu primera compra: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Ahora que sabes cómo crear <strong>productos</strong>, te invitamos a registrar tu primera <strong>compra</strong> para habilitar disponibilidad del producto. Descubre cómo hacerlo aquí:
                      <a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.Spanish)} title="Guia para crear una compra en wicadu">Cómo crear una compra</a>`,
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas Comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El botón "Continuar" está deshabilitado</strong>: Asegúrate de que todos los campos esten completos, incluyendo el nombre del producto, tipo y categoría.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Nombre del producto ya existe</strong>: Cada producto debe tener un nombre único; intenta modificar el nombre ligeramente.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>No se puede encontrar un producto similar durante la búsqueda</strong>: Utiliza palabras clave que coincidan con los nombres de artículos existentes.',
            ],
          },
        ],
      },
    },
  },
  en: {
    seo_title: 'Create products',
    seo_description:
      'Learn step by step how to create and edit products in your store. A practical guide to add items, update details, and get your catalog ready to start selling in your online store.',
    seo_keywords:
      'create products, add product to store, wicadu product catalog, how to create a product, product setup, online store inventory, product name, product type, product category',
    page_id: PagesId.CreateItems,
    breadcrumbs: {
      home: 'Home',
      items_home: 'Products and inventory',
      create_items: 'Create products',
    },
    article: {
      title: 'Create products',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to manage <em>inventory</em> and <em>products</em> within the <a href=${process.env.STORES_WEBAPP_HOST}>wicadu Assistant</a>.`,
        '<strong>Buyers</strong> or <strong>Suppliers</strong> with permissions to <em>create products</em>, <em>update features</em>, and <em>set prices</em>.',
      ],
      about: {
        title: 'About creating products',
        content_type: ContentType.Paragraph,
        content: [
          '<strong>Creating products</strong> is one of the <em>first steps</em> in operating your <strong>online store.</strong> Each product represents an <b>individual offer</b> in your catalog and can be either a <em>physical item</em> or a <em>service</em>.',
          'In <strong>wicadu</strong>, the product creation process is designed to be <strong>fast</strong>, <strong>flexible</strong>, and <strong>efficient</strong>. It allows you to <em>keep your inventory up to date</em>, <em>organize your catalog strategically</em>, and provide <em>clear and accurate information</em> to customers across all your sales channels.',
          `By structuring your products correctly from the beginning, you improve your store's <strong>visibility</strong>, <em>reduce operational errors</em>, and provide a <strong>better shopping experience</strong>.`
        ],
      },
      before_begin: {
        title: 'Before you begin ',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Before creating products in your store, make sure you have considered the following:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Fragment,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Your store's currency must be configured, since prices and stock will be managed in that currency.`
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      `<strong>Note</strong>: A default currency is automatically set based on your store's country when it is created.`,
                    ],
                  },

                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  '<em>Product modifiers</em> (like dimensions, weight, or materials) are optional, but recommended for better clarity and shipping configuration.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'You cannot add stock or register purchases until the product has been created.'
                ],
              },
            ],
          },
        ],
      },
      how_to: {
        title: 'Steps to create a product',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Go to Inventory module: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Access the <em>Inventory</em> functionality inside the <strong>wicadu Assistant</strong>.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Create a new product: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click the <b>+</b> button located in the upper right corner of the page.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Identify the product: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `On the <em>"Identify Product"</em> screen, enter your product's name. The system will search your catalog for existing products with similar modifiers that you can use as a base. You can then:`
                ],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>Duplicate product</b>: Select an existing product to use it as template and pre-fill the fields',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<b>Create</b>: Click “None of the above” to manually fill all required fields from scratch',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Note</strong>: <em>Product names must be unique</em>. You can create variants, but each product entry needs a different name.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Fill in the required fields: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'In the <em>"Create Product"</em> screen, complete or update the required fields based on your previous selection. Once all mandatory fields are filled, the <b>Continue</b> button will be enabled.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Review and complete: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Review the product details and click <b>Continue</b> to complete the process. You will be redirected to the <em>Product Details</em> page, where you can view or update more information.`
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Next Steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Now that you know how to create and edit the general details of a product, consider the following actions:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Add a product image: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // ARTICLE IDEA
                      'Add an image to your product to improve customer recognition.'
                      // `Add an image to your product to improve customer recognition. See more
                      // <a href=${getUrlByPageId(PagesId.ItemsHome, Languages.English)} title="Guía to add images to products">How to add images to a products</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Set a price: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Assign a <em>price</em> to your product to enable sales.'
                      // `Asigna un <em>precio</em> a tu producto para habilitar las ventas. Aprende más sobre
                      // <a href=${getUrlByPageId(PagesId.ItemsHome, Languages.English)} title="Guía para configurar precios">cómo configurar precios</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Edit product: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Edit the product information as needed. Learn more about
                      <a href=${getUrlByPageId(PagesId.UpdateItems, Languages.English)} title="How to edit and update products guide">How to edit products</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Add your first Purchase: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Now that you know how to create <strong>products</strong>, we invite you to register your first <strong>purchase</strong>. Learn how to do it here:
                      <a href="${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.English)}" title="Create a Manual Order in wicadu">How to add a purchase</a>`,
                    ],
                  },
                ],
              },

            ],
          },
        ],
      },
      common_issues: {
        title: 'Common Issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>The “Continue” button is disabled</strong>: Ensure all required fields are filled out, including product name, type, and category.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Product name already exists</strong>: Each product must have a unique name; try modifying it slightly.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>No similar product is found during search</strong>: Use keywords that better match existing product names.',
            ],
          },
        ],
      },
    },
  },
  fr: {
    seo_title: 'Créer des produits',
    seo_description:
      'Découvrez comment créer et configurer des produits dans votre boutique étape par étape afin de préparer votre catalogue pour la vente.',
    seo_keywords:
      "créer des produits boutique en ligne, ajouter des produits, gérer les produits wicadu, éditer des produits, catalogue produits, gestion d'inventaire e-commerce",
    page_id: PagesId.CreateItems,
    breadcrumbs: {
      home: 'Accueil',
      items_home: 'Produits et inventaire',
      create_items: 'Créer des produits',
    },
    article: {
      title: 'Créer des produits',
      intended: [
        `<strong>Propriétaires</strong> et <strong>administrateurs</strong> qui gèrent l’<em>inventaire</em> et les <em>produits</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a>.`,
        `<strong>Vendeurs</strong> ou membres de l’équipe d’approvisionnement autorisés à <em>créer des produits</em>, <em>mettre à jour leurs caractéristiques</em> et <em>définir des prix</em>.`,
      ],
      about: {
        title: 'À propos de la création de produits',
        content_type: ContentType.Paragraph,
        content: [
          'Créer un <strong>produit</strong> est l’une des premières étapes pour mettre en place une <strong>boutique en ligne</strong> bien organisée. Chaque produit représente une offre individuelle dans votre catalogue et constitue la base de votre gestion d’<strong>inventaire</strong>, de votre tarification et de vos futures <strong>ventes</strong>.',
          'Dans <strong>wicadu</strong>, le flux de création de produit est conçu pour vous permettre d’ajouter rapidement les informations principales, de structurer votre catalogue, de préparer les variantes et d’améliorer la qualité de vos fiches produit. Une bonne création de produit facilite ensuite la gestion du <strong>stock</strong>, des <strong>achats</strong>, des images et des prix.',
        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Avant de créer des produits dans votre boutique, assurez-vous d’avoir pris en compte les éléments suivants :',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Fragment,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'La <strong>devise</strong> de votre boutique doit être configurée, car les prix et le stock seront gérés dans cette devise.',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Remarque</strong> : une devise par défaut est automatiquement définie selon le pays sélectionné lors de la création de la boutique.',
                    ],
                  },
                ],
              },
              { content_type: ContentType.Paragraph, content: ['Les <em>spécifications du produit</em> (comme les dimensions, le poids ou les matériaux) sont facultatives, mais recommandées pour une meilleure clarté et une configuration d’expédition plus précise.'] },
              { content_type: ContentType.Paragraph, content: ['Vous ne pourrez pas ajouter de stock ni enregistrer des achats tant que le produit n’aura pas été créé.'] },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Ces étapes permettent de s’assurer que les <strong>produits</strong> sont prêts à être inclus dans vos <strong>achats</strong>.',
            ],
          },
        ],
      },
      how_to: {
        title: 'Étapes pour créer un produit',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Accéder au module Inventaire : '] },
              { content_type: ContentType.Paragraph, content: ['Ouvrez la fonctionnalité <em>Inventaire</em> dans l’<strong>assistant wicadu</strong>.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Créer un nouveau produit : '] },
              { content_type: ContentType.Paragraph, content: ['Cliquez sur le bouton <b>+</b> en haut à droite de la page.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Identifier le produit : '] },
              { content_type: ContentType.Paragraph, content: ['Sur l’écran <em>"Identifier le produit"</em>, saisissez le nom de votre produit. Le système recherchera dans votre catalogue les produits existants avec des modificateurs similaires que vous pourrez utiliser comme base. Vous pourrez ensuite :'] },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  { content_type: ContentType.Paragraph, content: ['<b>Dupliquer un produit</b> : sélectionnez un produit existant pour l’utiliser comme modèle et préremplir les champs.'] },
                  { content_type: ContentType.Paragraph, content: ['<b>Créer</b> : cliquez sur "Aucun des précédents" pour remplir manuellement tous les champs requis à partir de zéro.'] },
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: ['<strong>Remarque</strong> : <em>les noms de produits doivent être uniques</em>. Vous pouvez créer des variantes, mais chaque fiche produit doit avoir un nom différent.'],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Renseigner les champs obligatoires : '] },
              { content_type: ContentType.Paragraph, content: ['Dans l’écran <em>"Créer un produit"</em>, complétez ou mettez à jour les champs requis selon votre sélection précédente. Une fois tous les champs obligatoires remplis, le bouton <b>Continuer</b> sera activé.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Vérifier et finaliser : '] },
              { content_type: ContentType.Paragraph, content: ['Vérifiez les détails du produit puis cliquez sur <b>Continuer</b> pour terminer le processus. Vous serez redirigé vers la page <em>Détails du produit</em>, où vous pourrez consulter ou mettre à jour plus d’informations.'] },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Maintenant que vous savez comment créer et modifier les détails généraux d’un produit, envisagez les actions suivantes :',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Ajouter une image produit : '] },
                  { content_type: ContentType.Paragraph, content: ['Ajoutez une <strong>image produit</strong> pour améliorer sa reconnaissance par les clients.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Définir un prix : '] },
                  { content_type: ContentType.Paragraph, content: ['Attribuez un <em>prix</em> à votre produit pour activer les ventes.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Modifier le produit : '] },
                  { content_type: ContentType.Paragraph, content: [`Modifiez les informations du produit selon vos besoins. Découvrez-en plus dans
                      <a href=${getUrlByPageId(PagesId.UpdateItems, Languages.French)} title="Guide pour modifier des produits">Comment modifier des produits</a>.`] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Enregistrer votre premier achat : '] },
                  { content_type: ContentType.Paragraph, content: [`Maintenant que vous savez créer des <strong>produits</strong>, nous vous invitons à enregistrer votre premier <strong>achat</strong>. Découvrez comment faire ici :
                      <a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.French)} title="Guide pour créer un achat dans wicadu">Comment ajouter un achat</a>`] },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Le bouton "Continuer" est désactivé</strong> : assurez-vous que tous les champs obligatoires sont remplis, y compris le nom du produit, le type et la catégorie.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Le nom du produit existe déjà</strong> : chaque produit doit avoir un nom unique ; essayez de le modifier légèrement.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Aucun produit similaire n’est trouvé pendant la recherche</strong> : utilisez des mots-clés qui correspondent davantage aux noms de produits existants.',
            ],
          },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
